/**
 * Stock Verify Schedule Screen (5.1.1)
 * Inventory Module - Stock Verify
 * Connected to Live API
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, X, Save, RefreshCw, AlertCircle, Calendar } from 'lucide-react';
import { stockVerifySchedule } from '../../../services/api/inventory/inventoryService';

const StockVerifyScheduleScreen = ({ theme = 'dark' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    ScheduleName: '',
    Frequency: 'Monthly',
    NextDate: '',
    BranchId: 1
  });

  const isDark = theme === 'dark';
  const bgColor = isDark ? 'bg-slate-900' : 'bg-gray-50';
  const cardBg = isDark ? 'bg-slate-800' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';
  const inputBg = isDark ? 'bg-slate-700' : 'bg-gray-50';

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await stockVerifySchedule.getAll();
      setData(response.data?.data || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading schedules:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = () => {
    setEditItem(null);
    setFormData({ ScheduleName: '', Frequency: 'Monthly', NextDate: '', BranchId: 1 });
    setShowForm(true);
  };

  const handleEdit = async (item) => {
    try {
      const response = await stockVerifySchedule.getById(item.ScheduleId);
      const detail = response.data?.data || item;
      setEditItem(detail);
      setFormData({
        ScheduleName: detail.ScheduleName || '',
        Frequency: detail.Frequency || 'Monthly',
        NextDate: detail.NextDate || '',
        BranchId: detail.BranchId || 1
      });
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching item details:', err);
    }
  };

  const handleDelete = async (item) => {
    if (window.confirm(`Are you sure you want to delete "${item.ScheduleName}"?`)) {
      try {
        await stockVerifySchedule.delete(item.ScheduleId);
        loadData();
      } catch (err) {
        alert('Failed to delete. Please try again.');
      }
    }
  };

  const handleSave = async () => {
    try {
      if (editItem) {
        await stockVerifySchedule.update({ ...formData, ScheduleId: editItem.ScheduleId });
      } else {
        await stockVerifySchedule.save(formData);
      }
      setShowForm(false);
      loadData();
    } catch (err) {
      alert('Failed to save. Please try again.');
    }
  };

  const filteredData = data.filter(item =>
    Object.values(item).some(val =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const getFrequencyBadge = (frequency) => {
    const colors = {
      'Daily': 'bg-blue-500/20 text-blue-400',
      'Weekly': 'bg-purple-500/20 text-purple-400',
      'Monthly': 'bg-emerald-500/20 text-emerald-400',
      'Quarterly': 'bg-orange-500/20 text-orange-400',
      'Yearly': 'bg-red-500/20 text-red-400'
    };
    return colors[frequency] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} p-6`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Stock Verify Schedule</h1>
          <p className="text-sm text-gray-400">Screen ID: 5.1.1 | Inventory Module</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadData} className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={handleAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-btn">
            <Plus size={18} />
            Add Schedule
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-3">
          <AlertCircle className="text-red-400" size={20} />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className={`${cardBg} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Calendar className="text-blue-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Schedules</p>
              <p className="text-xl font-bold">{data.length}</p>
            </div>
          </div>
        </div>
        <div className={`${cardBg} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Calendar className="text-emerald-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-400">Active</p>
              <p className="text-xl font-bold">{data.filter(d => d.Status === 'Active').length}</p>
            </div>
          </div>
        </div>
        <div className={`${cardBg} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/20">
              <Calendar className="text-orange-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-400">Monthly</p>
              <p className="text-xl font-bold">{data.filter(d => d.Frequency === 'Monthly').length}</p>
            </div>
          </div>
        </div>
        <div className={`${cardBg} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Calendar className="text-purple-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-400">Quarterly</p>
              <p className="text-xl font-bold">{data.filter(d => d.Frequency === 'Quarterly').length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className={`${cardBg} rounded-lg p-4 mb-6 border ${borderColor}`}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search schedules..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Data Grid */}
      <div className={`${cardBg} rounded-lg border ${borderColor} overflow-hidden`}>
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
            <p>Loading...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="data-table">
              <thead className={isDark ? 'bg-slate-700' : 'bg-gray-100'}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">SL</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Schedule Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Frequency</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Next Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <tr key={item.ScheduleId} className={`border-t ${borderColor} hover:${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                    <td className="px-4 py-3">{index + 1}</td>
                    <td className="px-4 py-3 font-medium">{item.ScheduleName}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${getFrequencyBadge(item.Frequency)}`}>
                        {item.Frequency}
                      </span>
                    </td>
                    <td className="px-4 py-3">{item.NextDate}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        item.Status === 'Active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {item.Status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => handleEdit(item)}
                          className="p-1.5 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" 
                          data-testid={`edit-${item.ScheduleId}`}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button 
                          onClick={() => handleDelete(item)}
                          className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30" 
                          data-testid={`delete-${item.ScheduleId}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">No schedules found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`${cardBg} rounded-xl p-6 w-full max-w-md border ${borderColor}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{editItem ? 'Edit Schedule' : 'Add Schedule'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Schedule Name</label>
                <input
                  type="text"
                  value={formData.ScheduleName}
                  onChange={(e) => setFormData({ ...formData, ScheduleName: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
                  data-testid="form-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Frequency</label>
                <select
                  value={formData.Frequency}
                  onChange={(e) => setFormData({ ...formData, Frequency: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
                  data-testid="form-frequency"
                >
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Next Date</label>
                <input
                  type="date"
                  value={formData.NextDate}
                  onChange={(e) => setFormData({ ...formData, NextDate: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
                  data-testid="form-date"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="form-save"
              >
                <Save size={16} />
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockVerifyScheduleScreen;
